import React, { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { Button, Icon, IconButton, Snackbar, TablePagination, Tooltip } from '@mui/material';

import DialogAnnotationAdd from '../../controls/dialogs/DialogAnnotationAdd';
import DialogTimeline from '../../controls/dialogs/DialogTimeline';

import ProgressBar from '../../controls/ProgressBar';
import ErrorMessage from '../../controls/ErrorMessage';
import { ListParams } from '../../../models/listParams';
import { Annotation } from '../../../models/portfolio/annotation';
import { PagedResult } from '../../../models/pagedResult';
import AnnotationReplies from './AnnotationReplies';

import { portfolioService } from '../../../services/portfolio.service';
import { helperService } from '../../../services/helper.service';
import { eventBus } from '../../../services/event-emitter.service';
import { usePersistedPageSize } from '../../../hooks/usePersistedPageSize';
import { pageSizeStorageKey } from '../../../services/utils/localStorageKeys';


interface OverzichtAnnotationsProps {
  portfolioId?: string;
  mijnOrganisatieId?: string;
  organizations?: any[];
  refreshKey?: number;
  readOnly?: boolean;
}

interface AnnotationResponse extends PagedResult<Annotation> {}

const OverzichtAnnotations: React.FC<OverzichtAnnotationsProps> = ({
  portfolioId,
  mijnOrganisatieId,
  organizations,
  refreshKey,
  readOnly = false,
}) => {
  const { accounts } = useMsal();
  const account = accounts[0];

  const [selectedAnnotation, setSelectedAnnotation] = useState<Annotation | undefined>(undefined);
  const [annotationPrefill, setAnnotationPrefill] = useState<Partial<Annotation> | undefined>(undefined);
  const [totalCount, setTotalCount] = useState(0);
  const [annotations, setAnnotations] = useState<AnnotationResponse>({
    count: 0,
    offset: 0,
    limit: 10,
    items: []
  });
  const [page, setPage] = useState(0);
  const { pageSize, setPageSize } = usePersistedPageSize(pageSizeStorageKey('portfolio.notities'));
  const [expandedReplies, setExpandedReplies] = useState<Record<string, boolean>>({});

  const [parentAnnotationId, setParentAnnotationId] = useState<string | null>(null);
  const [archivedAnnotations, setArchivedAnnotations] = useState<Annotation[]>([]);

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [open, setOpenDialogs] = useState({
    annotation: false,
    timeline: false,
    snackbar: false
  });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);

  // Handlers to open/close each dialog
  const openDialog = (dialog: keyof typeof open) => {
    // setSelectedId(id);
    setOpenDialogs((prev) => ({ ...prev, [dialog]: true }));
  };

  const closeDialog = (dialog: keyof typeof open) => {
    setOpenDialogs((prev) => ({ ...prev, [dialog]: false }));
  };

  // // Handlers for menu
  // const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, annotation: Annotation, parentAnnotationId?: string) => {
  //   setSelectedAnnotation({ ...annotation, parentAnnotationId: parentAnnotationId || null });
  //   setAnchorEl(event.currentTarget);
  // };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    fetchAnnotations(page * pageSize, pageSize);
  }, [page, pageSize, portfolioId, refreshKey]);

  useEffect(() => {
    eventBus.on('openDialog', triggerOpenDialog);
    eventBus.on('openAnnotationPrefill', handleOpenAnnotationPrefill);

    // Cleanup
    return () => {
      eventBus.off('openDialog', triggerOpenDialog);
      eventBus.off('openAnnotationPrefill', handleOpenAnnotationPrefill);
    };
  }, []);

  const triggerOpenDialog = (dialog: string) => {
    if (dialog) {
      openDialog(dialog as keyof typeof open);
    }
  };

  const handleOpenAnnotationPrefill = (annotation: { title?: string; description?: string }) => {
    if (readOnly) {
      return;
    }

    setSelectedAnnotation(undefined);
    setParentAnnotationId(null);
    setAnnotationPrefill({
      title: annotation?.title ?? '',
      description: annotation?.description ?? '',
    });
    openDialog('annotation');
  };

  const fetchAnnotations = async (offset = 0, limit = 5) => {
    if (portfolioId) {
      try {
        setLoading(true);

        const params: ListParams = {
          offset,
          limit
        };

        const data = await portfolioService.getAnnotationsByPortfolio(portfolioId, params);

        if (data) {
          // const newItems = offset === 0 ? data.items : [...annotations.items, ...data.items];

          setAnnotations({
            ...data,
            items: data.items,
          });
          setTotalCount(data.count);
        }

        setLoading(false);
      }
      catch (error: any) {
        setError(error.message || 'Er is een fout opgetreden');
        setLoading(false);
      }
    }
  };

  const onClickAddAnnotation = async () => {
    if (readOnly) {
      return;
    }
    setAnnotationPrefill(undefined);
    setParentAnnotationId(null);
    openDialog('annotation');
  };

  const onClickEditAnnotation = async (annotation: Annotation) => {
    if (readOnly) {
      return;
    }
    // if (selectedAnnotation) {
    if (annotation) {
      setAnnotationPrefill(undefined);
      setSelectedAnnotation(annotation);
      openDialog('annotation');
      handleMenuClose();
    }
  };

  const onClickHistoryAnnotation = async (annotation: Annotation) => {
    if (annotation) {
      await fetchAnnotationsArchived(annotation.id || '');
      openDialog('timeline');
      handleMenuClose();
    }
  };

  const fetchAnnotationsArchived = async (annotationId: string) => {
    try {
      if (annotationId) {
        setLoading(true);

        const data = await portfolioService.getAnnotationsArchived(annotationId) as any;
        if (data) {
          // console.log('Fetched archived annotations:', data);

          const sortedItems = [...data.items].sort((a, b) =>
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
          );

          setArchivedAnnotations(sortedItems);
        }

        setLoading(false);
      }
    }
    catch (error: any) {
      setError(error.message || 'Er is een fout opgetreden');
      setLoading(false);
    }
  };

  const toggleRepliesVisible = (annotation: Annotation) => {
    setExpandedReplies((prev) => ({
      ...prev,
      [annotation.id as string]: !prev[annotation.id as string],
    }));
  };

  const handleSubmitAnnotation = async (annotation: Annotation) => {
    if (readOnly) {
      return;
    }
    try {
      setLoading(true);
      if (annotation) {

        let response;
        if (annotation.id) {
          response = await portfolioService.putAnnotation(annotation);
        }
        else {
          response = await portfolioService.postAnnotation(annotation);
        }

        // response.ok
        if (response && response.id) {
          setTimeout(() => {
            fetchAnnotations();

            setLoading(false);
            setSnackbarMessage('Notitie succesvol opgeslagen');
            openDialog('snackbar');
          }, 2000);
        }
      }
      else {
        console.error('Geen notitie om toe te voegen');
      }
      
    }
    catch (error) {
      setLoading(false);
      console.error('Error updating annotation:', error);
      setError('Er is een fout opgetreden');
    }
  };

  const getOrganisatieNaam = (organisatieId: string) => {
    const organisatie = organizations?.find(org => org.id === organisatieId);
    return organisatie?.name ?? organisatieId; // fallback to ID if name not found
  };

  return (
    <>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={open.snackbar}
        autoHideDuration={2000}
        onClose={() => closeDialog('snackbar')}
        message={snackbarMessage ? snackbarMessage : 'Er is een fout opgetreden'}
        key={'topcenter'}
      />

      <div className="component-header">
        <h4>
          <Icon>comment</Icon> Notities 
          {/* ({totalCount}) */}
        </h4>
        {!readOnly && (
          <div className="btn-group">
            <Tooltip title="Nieuwe notitie toevoegen" placement="right" arrow>
              <Button variant="text" color="secondary" onClick={ () => onClickAddAnnotation() }>
                <Icon>add</Icon>
                Notitie
              </Button>
            </Tooltip>
          </div>
        )}
      </div>

      {/* <Menu
        anchorEl={anchorEl}
        open={openMenu}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem
          onClick={() => onClickEditAnnotation() }
        >
          <ListItemIcon>
            <Icon>edit</Icon>
          </ListItemIcon>
          Wijzigen
        </MenuItem>
        <MenuItem
          onClick={() => onClickHistoryAnnotation() }
        >
          <ListItemIcon>
            <Icon>timeline</Icon>
          </ListItemIcon>
          Toon historie
        </MenuItem>
      </Menu> */}

      <div className="annotations">
        {annotations.count === 0 && annotations.items.length === 0 && (
          <div className="annotation-thread">
            <div className="annotation-card" style={{ paddingBottom: 0 }}>
              <div className="annotation-header">
                <p>Er zijn nog geen notities</p>
              </div>
            </div>
          </div>
        )}

        {(annotations.items ?? []).map(annotation => (
          <div key={annotation.id} className="annotation-thread">

            <div className="annotation-card">

              <div className="annotation-header">
                {annotation.title && <h4>{annotation.title}</h4>}

                {/* <div className="annotation-actions">
                  <IconButton
                    className="btn-annotation-more"
                    onClick={(e) => handleMenuOpen(e, annotation)}
                  >
                    <Tooltip title="Acties" placement="right" arrow>
                      <Icon>more_vert</Icon>
                    </Tooltip>
                  </IconButton>
                </div> */}
              </div>

              <p>{annotation.description}</p>

              <div className="annotation-footer">
                <div className="meta">
                  {/* <Tooltip 
                    title={`Geplaatst namens: ${getOrganisatieNaam(annotation.createdByOrganization ?? '')}`}
                    arrow
                  >
                    <div className="organization">{getOrganisatieNaam(annotation.createdByOrganization ?? '')}</div>
                  </Tooltip> */}
                  <Tooltip
                    title={
                      <div>
                        Geplaatst door: {annotation.createdByUser?.toLowerCase() ?? 'Onbekend gebruiker'}
                        <br />
                        namens {getOrganisatieNaam(annotation.createdByOrganization ?? '')}
                      </div>
                    }
                    arrow
                  >
                    <div className="user">{annotation.createdByUser?.toLowerCase() ?? 'Onbekend gebruiker'}</div>                    
                  </Tooltip>
                  <Tooltip
                    title={`Geplaatst op: ${helperService.formatDutchDate(annotation.timestamp)}`}
                    arrow
                  >
                    <div className="date">{helperService.formatDutchDate(annotation.timestamp)}</div>
                  </Tooltip>

                  {annotation.children && annotation.children.count > 0 ? (
                    <div className="replies">
                      <Button
                        variant="text"
                        color="secondary"
                        className="btn-small"
                        onClick={() => toggleRepliesVisible(annotation)}
                      >
                        {annotation.children?.count === 1
                          ? '1 opmerking'
                          : `${annotation.children?.count || 0} opmerkingen`
                        }
                        <Icon>
                          {annotation.id && expandedReplies[annotation.id] ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
                        </Icon>
                      </Button>
                    </div>
                  ) : (
                    <div className="replies">Geen opmerkingen</div>
                  )}
                </div>

                <div className="actions">
                  {/* <Tooltip title="Opmerking toevoegen" arrow>
                    <IconButton color="secondary" size="small" onClick={() => onClickAddReply(annotation.id)}>
                      <Icon>add</Icon>
                    </IconButton>
                  </Tooltip> */}

                  {!readOnly && (
                    <Tooltip title="Voeg een opmerking toe" arrow>
                      <IconButton
                        color="secondary"
                        size="small"
                        onClick={() => {
                          toggleRepliesVisible(annotation);  // open replies if closed
                          // onClickAddReply(annotation.id);
                        }}
                      >
                        <Icon>reply</Icon>
                      </IconButton>
                    </Tooltip>
                  )}

                  <Tooltip title="Bekijk historie" arrow>
                    <IconButton color="secondary" size="small" onClick={() => onClickHistoryAnnotation(annotation)}>
                      <Icon>timeline</Icon>
                    </IconButton>
                  </Tooltip>

                  {!readOnly && annotation.createdByOrganization === mijnOrganisatieId && (
                    <Tooltip title="Wijzigen notitie" arrow>
                      <IconButton color="secondary" size="small" onClick={() => onClickEditAnnotation(annotation)}>
                        <Icon>edit</Icon>
                      </IconButton>
                    </Tooltip>
                  )}
                </div>
              </div>
            </div>

            <AnnotationReplies
              portfolioId={portfolioId}
              object={annotation}
              type='annotation'
              visible={annotation.id != null ? expandedReplies[annotation.id] : false}
              account={account}
              organizations={organizations}
              mijnOrganisatieId={mijnOrganisatieId}
              readOnly={readOnly}
            />

          </div>
        ))}

        <TablePagination
          component="div"
          count={annotations.count}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => {
            setPageSize(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          labelRowsPerPage="Notities per pagina:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} van ${count}`}
        />

        {/* {annotations.count > annotations.items.length && (
          <div className="btn-group">
            <Button
              variant="text"
              color="secondary"
              className="btn-more"
              onClick={() => annotations.count && annotations.items.length && fetchAnnotations(annotations.items.length, annotations.limit)
              }
            >
              <Icon>unfold_more</Icon>
              Toon nog {annotations.count - annotations.items.length} notities
            </Button>
          </div>
        )} */}

      </div>

      <DialogAnnotationAdd
        open={open.annotation}
        portfolioId={portfolioId}
        selectedAnnotation={selectedAnnotation}
        initialAnnotation={annotationPrefill}
        parentId={parentAnnotationId ?? undefined}
        onClose={ () => {
          closeDialog('annotation');
          setAnnotationPrefill(undefined);
        } }
        onSubmit={handleSubmitAnnotation}
      />

      {archivedAnnotations.length > 0 && (
        <DialogTimeline
          open={open.timeline}
          items={archivedAnnotations as any[]}
          type="annotation"
          title={`Historie van deze notitie: ${selectedAnnotation ? selectedAnnotation.title : ''}`}
          organizations={organizations}
          onClose={ () => closeDialog('timeline') }
        />
      )}

      { loading && (
        <ProgressBar />
      )}

      { error && (
        <ErrorMessage message={error} />
      )}
    </>
  );
};

export default OverzichtAnnotations;
